package org.example.project.engineslot

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.dashboard.PopupEngineCard
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun SlotSetupBookingPopup(
    booking: All,
    masterData: List<All>,
    standorte: List<All>,
    zugmaschinen: List<All> = emptyList(),
    auflieger: List<All> = emptyList(),
    showActions: Boolean = true,
    onClose: () -> Unit,
    onDelete: () -> Unit,
    onBookTargetSlot: ((All) -> Unit)? = null,
    onSave: (All) -> Unit
) {
    var state by remember(booking, standorte) {
        mutableStateOf(
            booking.copy(
                slotAbholort = slotSelectedAbholort(booking, standorte),
                slotStandortId = booking.slotStandortId.ifBlank { booking.standort }
            )
        )
    }
    var selectedBereich by remember(booking.id, booking.auftragsart) {
        mutableStateOf(
            slotBookingArtCode(booking)
                .takeIf { it == SLOT_BEREICH_WA || it == SLOT_BEREICH_WE }
        )
    }
    val popupWidthPx = if (selectedBereich == SLOT_BEREICH_WE) 1280 else 920
    val popupMinWidthPx = if (selectedBereich == SLOT_BEREICH_WE) 1180 else 840
    val effectivePopupWidthPx = if (selectedBereich == SLOT_BEREICH_WA && slotBookingIsSammelWa(state)) 1280 else popupWidthPx
    val effectivePopupMinWidthPx = if (selectedBereich == SLOT_BEREICH_WA && slotBookingIsSammelWa(state)) 1180 else popupMinWidthPx

    PopupEngineCard(
        windowKey = "slot-setup-booking-v2-${booking.id}",
        widthPx = effectivePopupWidthPx,
        heightPx = null,
        minWidthPx = effectivePopupMinWidthPx,
        resizable = true,
        onClose = onClose
    ) {
        SlotBookingCardScaffold(
            state = state,
            masterData = masterData,
            standorte = standorte,
            zugmaschinen = zugmaschinen,
            auflieger = auflieger,
            readOnly = false,
            showSelectionStep = selectedBereich == null,
            onSelectBereich = { bereich ->
                selectedBereich = bereich
                state = state.copy(
                    auftragsart = bereich,
                    slotBereich = bereich
                )
            },
            actionContent = if (showActions) {
                {
                    val endToEndTargetWe = selectedBereich == SLOT_BEREICH_WE && state.endToEndFlow && state.flowTyp == "wa_to_we"
                    if (selectedBereich == SLOT_BEREICH_WE && !endToEndTargetWe) {
                        LumaticaButton(
                            "Lieferposition hinzufuegen",
                            onClick = { state = slotLiefernAppendBeleg(state) },
                            variant = LumaticaButtonVariant.Standard
                        )
                    } else if (selectedBereich == SLOT_BEREICH_WA) {
                        LumaticaButton(
                            "Tour hinzufuegen",
                            onClick = { state = slotLadenAppendTour(state) },
                            variant = LumaticaButtonVariant.Standard
                        )
                    }
                    LumaticaButton("Schließen", onClick = onClose, variant = LumaticaButtonVariant.Standard)
                    if (
                        selectedBereich == SLOT_BEREICH_WA &&
                        state.zustand == ZustandManager.Slot.FREI &&
                        state.endToEndFlow &&
                        state.flowTargetFirmenId.isNotBlank() &&
                        !state.flowWeAvis
                    ) {
                        LumaticaButton(
                            "Ziel-Zeitfenster buchen",
                            onClick = { onBookTargetSlot?.invoke(state) },
                            variant = LumaticaButtonVariant.Action
                        )
                    }
                    if (slotHasBookingData(state)) {
                        LumaticaButton("Löschen", onClick = onDelete, variant = LumaticaButtonVariant.Standard)
                    }
                    LumaticaButton("Speichern", onClick = { onSave(state) }, variant = LumaticaButtonVariant.Action)
                }
            } else null,
            onStateChange = { state = it }
        )
    }
}

fun slotBookingPopupWidth(booking: All): Pair<Int, Int> {
    val bereich = slotBookingArtCode(booking)
    return if (bereich == SLOT_BEREICH_WE || slotBookingIsSammelWa(booking)) 1280 to 1180 else 920 to 840
}

@Composable
fun SlotBookingReadonlyFormCard(
    booking: All,
    masterData: List<All>,
    standorte: List<All>,
    zugmaschinen: List<All> = emptyList(),
    auflieger: List<All> = emptyList(),
    solidBackground: Boolean = false
) {
    val state = remember(booking, standorte) {
        booking.copy(
            slotAbholort = slotSelectedAbholort(booking, standorte),
            slotBereich = booking.slotBereich.ifBlank { booking.auftragsart },
            slotStandortId = booking.slotStandortId.ifBlank { booking.standort }
        )
    }
    SlotBookingCardScaffold(
        state = state,
        masterData = masterData,
        standorte = standorte,
        zugmaschinen = zugmaschinen,
        auflieger = auflieger,
        readOnly = true,
        solidBackground = solidBackground,
        showSelectionStep = false,
        actionContent = null,
        onStateChange = {}
    )
}

@Composable
private fun SlotBookingCardScaffold(
    state: All,
    masterData: List<All>,
    standorte: List<All>,
    zugmaschinen: List<All>,
    auflieger: List<All>,
    readOnly: Boolean,
    solidBackground: Boolean = false,
    showSelectionStep: Boolean,
    onSelectBereich: ((String) -> Unit)? = null,
    actionContent: (@Composable () -> Unit)? = null,
    onStateChange: (All) -> Unit
) {
    val abholortOptions = remember(standorte) { slotAbholortOptions(standorte) }
    val hasMultipleStandorte = abholortOptions.size > 1
    val selectedAbholort = slotSelectedAbholort(state, standorte)
    val selectedAbholortLabel = slotAbholortLabel(selectedAbholort, standorte)
    val popupHeaderPrefix = when (slotBookingArtCode(state)) {
        SLOT_BEREICH_WA -> "Laden"
        SLOT_BEREICH_WE -> "Liefern"
        else -> ""
    }
    val popupHeader = listOf(
        popupHeaderPrefix,
        "Zeitfenster: ${state.uhrzeitVon} bis ${state.uhrzeitBis} Uhr"
    ).filter { it.isNotBlank() }.joinToString(" | ")

    Div({
        classes(if (solidBackground) SlotStyles.hoverPanel else SlotStyles.glassPanel)
        style {
            width(100.percent)
            height(100.percent)
            padding(30.px, 30.px, 30.px, 30.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            boxSizing("border-box")
        }
    }) {
        Span({
            classes(SlotStyles.sectionTitle)
            style {
                textAlign("center")
                marginBottom(30.px)
            }
        }) { Text(popupHeader) }

        if (showSelectionStep) {
            SlotBookingBereichSelection(
                onSelect = { bereich -> onSelectBereich?.invoke(bereich) }
            )
        } else {
            actionContent?.let { content ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        alignItems(AlignItems.Center)
                        gap(12.px)
                        minHeight(56.px)
                        padding(0.px)
                        marginBottom(30.px)
                        flexWrap(FlexWrap.Wrap)
                        boxSizing("border-box")
                    }
                }) {
                    content()
                }
            }

            when (slotBookingArtCode(state)) {
                SLOT_BEREICH_WE -> SlotBookingLiefernContent(
                    state = state,
                    masterData = masterData,
                    standorte = standorte,
                    zugmaschinen = zugmaschinen,
                    auflieger = auflieger,
                    hasMultipleStandorte = hasMultipleStandorte,
                    selectedAbholort = selectedAbholort,
                    selectedAbholortLabel = selectedAbholortLabel,
                    readOnly = readOnly,
                    onStateChange = onStateChange
                )
                else -> SlotBookingLadenContent(
                    state = state,
                    masterData = masterData,
                    standorte = standorte,
                    zugmaschinen = zugmaschinen,
                    auflieger = auflieger,
                    hasMultipleStandorte = hasMultipleStandorte,
                    selectedAbholort = selectedAbholort,
                    selectedAbholortLabel = selectedAbholortLabel,
                    readOnly = readOnly,
                    onStateChange = onStateChange
                )
            }
        }
    }
}

@Composable
private fun SlotBookingBereichSelection(
    onSelect: (String) -> Unit
) {
    Div({
        style {
            flex(1)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            boxSizing("border-box")
            padding(40.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                gap(40.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            LumaticaButton(
                label = "Laden",
                onClick = { onSelect(SLOT_BEREICH_WA) },
                variant = LumaticaButtonVariant.Action,
                heightPx = 56,
                paddingH = 34
            )
            LumaticaButton(
                label = "Liefern",
                onClick = { onSelect(SLOT_BEREICH_WE) },
                variant = LumaticaButtonVariant.Action,
                heightPx = 56,
                paddingH = 34
            )
        }
    }
}

@Composable
fun SlotBookingFormFields(
    state: All,
    masterData: List<All>,
    standorte: List<All>,
    hasMultipleStandorte: Boolean,
    selectedAbholort: String,
    selectedAbholortLabel: String,
    onStateChange: (All) -> Unit
) {
    val abholortOptions = remember(standorte) { slotAbholortOptions(standorte) }

    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
            gap(12.px)
        }
    }) {
        DropdownField("Auftraggeber", slotDropdownOptions(masterData, SLOT_MASTER_AUFTRAGGEBER), state.auftraggeber) {
            onStateChange(state.copy(auftraggeber = it))
        }
        DropdownField("Unternehmer", slotDropdownOptions(masterData, SLOT_MASTER_LADEFIRMA), state.unternehmer) {
            onStateChange(state.copy(unternehmer = it))
        }
        DropdownField("Empfaenger", slotDropdownOptions(masterData, SLOT_MASTER_EMPFAENGER), state.empfaenger) {
            onStateChange(state.copy(empfaenger = it))
        }
        if (hasMultipleStandorte) {
            DropdownField("Abholort", abholortOptions, selectedAbholort) {
                onStateChange(
                    state.copy(
                        slotAbholort = it,
                        slotStandortId = it
                    )
                )
            }
        } else {
            ReadonlyField("Abholort", selectedAbholortLabel.ifBlank { "Kein Standort" })
        }
        SlotTextInput("lieferscheinnummer", state.lieferscheinnummer) { onStateChange(state.copy(lieferscheinnummer = it)) }
        SlotTextInput("Paletten", state.palettenanzahl.toString()) {
            onStateChange(state.copy(palettenanzahl = it.toIntOrNull() ?: 0))
        }
        SlotTextInput("LKW-Kennzeichen", state.lkwkennzeichen) { onStateChange(state.copy(lkwkennzeichen = it)) }
        ReadonlyField("Zustand", slotStatusLabel(state.zustand))
        slotBookingYardStatusValue(state).takeIf { it.isNotBlank() }?.let { status ->
            ReadonlyField("LKW-Zustand", status)
        }
    }
}

@Composable
fun ReadonlyField(label: String, value: String) {
    SlotField(label) {
        Div({
            style { slotInputStyle() }
        }) {
            Text(value)
        }
    }
}

@Composable
fun DropdownField(label: String, options: List<All>, selectedValue: String, onChange: (String) -> Unit) {
    SlotField(label) {
        AppDropdown(
            options = options,
            selectedValue = selectedValue,
            onChange = onChange,
            placeholder = "Auswählen",
            appearance = AppDropdownAppearance.Field,
            menuMode = AppDropdownMenuMode.Overlay
        )
    }
}

@Composable
fun SlotTextInput(label: String, value: String, onChange: (String) -> Unit) {
    SlotField(label) {
        Input(InputType.Text, attrs = {
            value(value)
            style { slotInputStyle() }
            onInput { onChange(it.value) }
        })
    }
}

